/**
 * Elige candidate de entrega (LAN DICOMweb vs R2 / WAN).
 * Contrato: docs/CONTRATO-VIEWER-TICKETS.md
 * El ticket configura order / timeout / skip_mixed_content. UDV no hardcodea hosts.
 */

import { probeEdgeLanHealth } from './gatewayPushPayload'

export const DEFAULT_PROBE = {
  lan_timeout_ms: 800,
  skip_mixed_content: true,
  order: ['lan', 'r2', 'wan'],
}

const ALLOWED_ORDER = { lan: true, r2: true, wan: true }

/**
 * @param {object} [raw]
 * @returns {{
 *   lan_timeout_ms: number,
 *   skip_mixed_content: boolean,
 *   order: string[],
 * }}
 */
export function normalizeTicketProbe(raw) {
  const data = raw && typeof raw === 'object' ? raw : {}
  let timeout = Number(
    data.lan_timeout_ms != null ? data.lan_timeout_ms : data.lanTimeoutMs
  )
  if (!Number.isFinite(timeout) || timeout <= 0) {
    timeout = DEFAULT_PROBE.lan_timeout_ms
  }
  if (timeout > 8000) timeout = 8000

  const skipRaw =
    data.skip_mixed_content != null
      ? data.skip_mixed_content
      : data.skipMixedContent
  const skipMixed =
    skipRaw === false ||
    skipRaw === 0 ||
    skipRaw === '0' ||
    skipRaw === 'false' ||
    skipRaw === 'no'
      ? false
      : true

  const rawOrder = Array.isArray(data.order) ? data.order : DEFAULT_PROBE.order
  const order = []
  for (let i = 0; i < rawOrder.length; i++) {
    const g = String(rawOrder[i] || '').toLowerCase()
    if (ALLOWED_ORDER[g] && order.indexOf(g) < 0) order.push(g)
  }
  return {
    lan_timeout_ms: timeout,
    skip_mixed_content: skipMixed,
    order: order.length ? order : DEFAULT_PROBE.order.slice(),
  }
}

/**
 * @param {object} c
 * @returns {number}
 */
function priorityOf(c) {
  const p = c && c.priority
  return typeof p === 'number' && !Number.isNaN(p) ? p : 100
}

/**
 * @param {object} c
 * @returns {boolean}
 */
function isLanDicomweb(c) {
  if (!c) return false
  const source = String(c.source || '').toLowerCase()
  const scope = String(c.scope || '').toLowerCase()
  return source === 'dicomweb' && scope === 'lan'
}

/**
 * @param {object} c
 * @returns {boolean}
 */
function isR2(c) {
  if (!c) return false
  const source = String(c.source || '').toLowerCase()
  return source === 'r2' || Boolean(c.manifest_url || c.manifestUrl)
}

/**
 * Grupo de probe.order: lan | r2 | wan.
 * @param {object} c
 * @returns {'lan'|'r2'|'wan'}
 */
export function candidateGroup(c) {
  if (isR2(c)) return 'r2'
  const scope = String((c && c.scope) || '').toLowerCase()
  if (scope === 'lan') return 'lan'
  if (scope === 'wan') return 'wan'
  if (isLanDicomweb(c)) return 'lan'
  return 'wan'
}

/**
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isHttpUrl(url) {
  return /^http:\/\//i.test(String(url == null ? '' : url).trim())
}

/**
 * @param {string|null|undefined} [explicit]
 * @returns {string}
 */
export function resolvePageProtocol(explicit) {
  if (explicit != null && String(explicit).trim() !== '') {
    return String(explicit)
  }
  try {
    if (
      typeof window !== 'undefined' &&
      window.location &&
      window.location.protocol
    ) {
      return window.location.protocol
    }
  } catch (e) {
    /* ignore */
  }
  return ''
}

/**
 * Página HTTPS + URL http:// → mixed content (el browser lo bloquea).
 * @param {string|null|undefined} url
 * @param {string} pageProtocol
 * @returns {boolean}
 */
export function isInsecureHttpOnHttpsPage(url, pageProtocol) {
  return String(pageProtocol) === 'https:' && isHttpUrl(url)
}

/**
 * Fallo de origen (no “estudio vacío”). Skip al siguiente candidate.
 * @param {unknown} err
 * @returns {boolean}
 */
export function isOriginFailure(err) {
  if (!err) return false
  const name = err.name != null ? String(err.name) : ''
  const msg = String((err && err.message) || err || '')
  const blob = `${name} ${msg}`
  if (name === 'AbortError') return true
  if (name === 'TypeError') return true
  // Manifest / origin HTTP: 401/403 (firma vencida, forbidden) → siguiente candidate
  if (/manifest\s*\(\s*40[13]\s*\)|no se pudo obtener el manifest\s*\(\s*40[13]\s*\)/i.test(blob)) {
    return true
  }
  if (/\b(401|403)\b/.test(blob) && /manifest|forbidden|unauthorized|enlace expirado/i.test(blob)) {
    return true
  }
  return /failed to fetch|mixed content|cors|access-control|preflight|err_connection|err_failed|err_address|err_timed_out|err_name_not_resolved|networkerror|load failed|aborted/i.test(
    blob
  )
}

/**
 * @param {object} c
 * @returns {string}
 */
export function candidateDeliveryUrl(c) {
  if (!c) return ''
  return String(
    c.wado_url ||
      c.wadoUrl ||
      c.manifest_url ||
      c.manifestUrl ||
      c.edge_lan_base ||
      c.edgeLanBase ||
      ''
  ).trim()
}

/**
 * Host:puerto de una URL (IP LAN o FQDN).
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function originHostFromUrl(url) {
  const raw = url != null ? String(url).trim() : ''
  if (!raw) return ''
  try {
    return new URL(raw).host || ''
  } catch (e) {
    return ''
  }
}

/**
 * RFC1918 / localhost / .local — no hardcodea clínicas.
 * @param {string|null|undefined} host
 * @returns {boolean}
 */
export function isPrivateLanHost(host) {
  const h = String(host || '')
    .trim()
    .toLowerCase()
    .split(':')[0]
  if (!h) return false
  if (h === 'localhost' || h.endsWith('.local')) return true
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h)) return true
  return false
}

function firstStudyUid(candidates) {
  const list = Array.isArray(candidates) ? candidates : []
  for (let i = 0; i < list.length; i++) {
    const c = list[i]
    if (!c) continue
    const uid =
      c.study_uid || c.studyUid || c.study_instance_uid || c.studyInstanceUID
    if (uid) return String(uid)
  }
  return ''
}

/**
 * Si edge_lan_base ya es DICOMweb/WADO, se usa; si es API Edge, se prueba /dicom-web.
 * @param {string|null|undefined} base
 * @returns {string}
 */
export function dicomWebUrlFromLanBase(base) {
  const raw = String(base == null ? '' : base)
    .trim()
    .replace(/\/+$/, '')
  if (!raw) return ''
  if (/\/(dicom-web|wado-rs|wado)(\/|$)/i.test(raw)) return raw
  return `${raw}/dicom-web`
}

/**
 * Si el ticket no mandó scope=lan pero hay edge_lan_base / edge_wado_url
 * con host privado, se agrega un candidate LAN para el walker.
 * @param {object[]|null|undefined} candidates
 * @param {{ edgeLanBase?: string|null, edgeWadoUrl?: string|null }} [opts]
 * @returns {object[]}
 */
export function augmentCandidatesWithLan(candidates, opts = {}) {
  const list = Array.isArray(candidates) ? candidates.slice() : []
  if (list.some((c) => candidateGroup(c) === 'lan')) return list
  const studyUid = firstStudyUid(list)
  if (!studyUid) return list
  const tryUrls = [
    opts.edgeWadoUrl,
    dicomWebUrlFromLanBase(opts.edgeLanBase),
  ]
  let rawWado = ''
  for (let i = 0; i < tryUrls.length; i++) {
    const s = tryUrls[i] != null ? String(tryUrls[i]).trim() : ''
    if (s && isPrivateLanHost(originHostFromUrl(s))) {
      rawWado = s
      break
    }
  }
  if (!rawWado) return list
  list.unshift({
    source: 'dicomweb',
    scope: 'lan',
    priority: 1,
    wado_url: rawWado,
    study_uid: studyUid,
  })
  return list
}

/**
 * Primer host LAN conocido (candidate o edge_*).
 * @param {object[]|null|undefined} candidates
 * @param {{ edgeLanBase?: string|null, edgeWadoUrl?: string|null }} [opts]
 * @returns {{ host: string, url: string }}
 */
export function collectLanHint(candidates, opts = {}) {
  const list = Array.isArray(candidates) ? candidates : []
  for (let i = 0; i < list.length; i++) {
    const c = list[i]
    if (candidateGroup(c) !== 'lan') continue
    const url = candidateDeliveryUrl(c)
    const host = originHostFromUrl(url)
    if (host) return { host, url }
  }
  const fromBase = originHostFromUrl(opts.edgeLanBase)
  if (fromBase) return { host: fromBase, url: String(opts.edgeLanBase).trim() }
  const fromWado = originHostFromUrl(opts.edgeWadoUrl)
  if (fromWado) return { host: fromWado, url: String(opts.edgeWadoUrl).trim() }
  return { host: '', url: '' }
}

/**
 * @param {object[]|null|undefined} candidates
 * @param {{ profile?: string|null, clientScope?: string|null }} [opts]
 * @returns {object[]}
 */
export function filterCandidatesForProfile(candidates, opts = {}) {
  const list = Array.isArray(candidates) ? candidates.slice() : []
  const profile = opts.profile != null ? String(opts.profile).toLowerCase() : ''
  // Solo portal paciente omite LAN. client_scope=wan es un hint (NAT/PWA)
  // y no debe esconder un candidate LAN clínico: el probe decide.
  if (profile !== 'patient') return list
  return list.filter((c) => {
    if (!c) return false
    if (candidateGroup(c) === 'lan') return false
    const source = String(c.source || '').toLowerCase()
    const scope = String(c.scope || '').toLowerCase()
    return source === 'r2' || scope === 'wan' || Boolean(c.manifest_url || c.manifestUrl)
  })
}

/**
 * @param {object[]|null|undefined} candidates
 * @returns {object[]}
 */
export function sortCandidatesByPriority(candidates) {
  return (Array.isArray(candidates) ? candidates.slice() : []).sort(
    (a, b) => priorityOf(a) - priorityOf(b)
  )
}

/**
 * @param {object[]|null|undefined} candidates
 * @param {string[]} [order]
 * @returns {object[]}
 */
export function sortCandidatesByProbeOrder(candidates, order) {
  const ord = Array.isArray(order) && order.length ? order : DEFAULT_PROBE.order
  const idx = (c) => {
    const i = ord.indexOf(candidateGroup(c))
    return i < 0 ? 1000 : i
  }
  return (Array.isArray(candidates) ? candidates.slice() : []).sort((a, b) => {
    const d = idx(a) - idx(b)
    if (d !== 0) return d
    return priorityOf(a) - priorityOf(b)
  })
}

/**
 * Probe reachability of a LAN WADO/DICOMweb base.
 * Un solo intento: TypeError / mixed content / CORS / abort → false (sin GET extra).
 * @param {string} wadoUrl
 * @param {{ timeoutMs?: number, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<boolean>}
 */
export async function probeLanReachable(wadoUrl, opts = {}) {
  const url = wadoUrl != null ? String(wadoUrl).trim() : ''
  if (!url) return false
  const timeoutMs =
    opts.timeoutMs != null ? opts.timeoutMs : DEFAULT_PROBE.lan_timeout_ms
  const fetchFn = opts.fetchImpl || fetch

  let abortCtrl = null
  let timer = null
  try {
    const init = { method: 'HEAD', mode: 'cors', cache: 'no-store' }
    if (typeof AbortController !== 'undefined') {
      abortCtrl = new AbortController()
      init.signal = abortCtrl.signal
      timer = setTimeout(function () {
        try {
          abortCtrl.abort()
        } catch (e) {
          /* ignore */
        }
      }, timeoutMs)
    }
    await fetchFn(url, init)
    return true
  } catch (e) {
    if (isOriginFailure(e)) return false
    try {
      const init2 = { method: 'GET', mode: 'cors', cache: 'no-store' }
      if (typeof AbortController !== 'undefined') {
        abortCtrl = new AbortController()
        init2.signal = abortCtrl.signal
        if (timer) clearTimeout(timer)
        timer = setTimeout(function () {
          try {
            abortCtrl.abort()
          } catch (err) {
            /* ignore */
          }
        }, timeoutMs)
      }
      await fetchFn(url, init2)
      return true
    } catch (e2) {
      return false
    }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * @param {string} wadoUrl
 * @param {string|null|undefined} edgeWadoUrl
 * @returns {boolean}
 */
export function hostsMatchEdge(wadoUrl, edgeWadoUrl) {
  if (!wadoUrl || !edgeWadoUrl) return false
  try {
    const a = new URL(wadoUrl)
    const b = new URL(edgeWadoUrl)
    return a.host === b.host
  } catch (e) {
    return false
  }
}

/**
 * @param {object} c
 * @param {string} pageProtocol
 * @param {string|null} edgeLanBase
 * @returns {boolean}
 */
function isLanHttpOnHttpsPage(c, pageProtocol) {
  if (String(pageProtocol) !== 'https:') return false
  const wado = c && (c.wado_url || c.wadoUrl)
  return isHttpUrl(wado)
}

/**
 * Elige el mejor candidate. No hardcodea hosts (visorweb, .189, …).
 * @param {object[]|null|undefined} candidates
 * @param {{
 *   profile?: string|null,
 *   clientScope?: string|null,
 *   edgeWadoUrl?: string|null,
 *   edgeLanBase?: string|null,
 *   probe?: boolean,
 *   probeConfig?: object|null,
 *   timeoutMs?: number,
 *   fetchImpl?: typeof fetch,
 *   pageProtocol?: string,
 * }} [opts]
 * @returns {Promise<{
 *   candidate: object|null,
 *   reason: string,
 *   remaining: object[],
 * }>}
 */
export async function pickViewerCandidate(candidates, opts = {}) {
  const probeCfg = normalizeTicketProbe(
    opts.probeConfig != null ? opts.probeConfig : opts.probe && typeof opts.probe === 'object' ? opts.probe : null
  )
  const pageProtocol = resolvePageProtocol(opts.pageProtocol)
  const augmented = augmentCandidatesWithLan(candidates, {
    edgeLanBase: opts.edgeLanBase,
    edgeWadoUrl: opts.edgeWadoUrl,
  })
  const filtered = filterCandidatesForProfile(augmented, {
    profile: opts.profile,
    clientScope: opts.clientScope,
  })
  const walkOrder = probeCfg.order.slice()
  if (
    filtered.some((c) => candidateGroup(c) === 'lan') &&
    walkOrder.indexOf('lan') < 0
  ) {
    walkOrder.unshift('lan')
  }
  const allowed = {}
  for (let o = 0; o < walkOrder.length; o++) {
    allowed[walkOrder[o]] = true
  }
  const inOrder = filtered.filter((c) => allowed[candidateGroup(c)])
  const sorted = sortCandidatesByProbeOrder(inOrder, walkOrder)
  const lanHint = collectLanHint(augmented, {
    edgeLanBase: opts.edgeLanBase,
    edgeWadoUrl: opts.edgeWadoUrl,
  })
  if (!sorted.length) {
    return {
      candidate: null,
      reason: 'no_candidates',
      remaining: [],
      lanHost: lanHint.host,
      lanSkipReason: '',
    }
  }

  const doProbe = opts.probe !== false
  const edgeLanBase =
    opts.edgeLanBase != null && String(opts.edgeLanBase).trim() !== ''
      ? String(opts.edgeLanBase).trim()
      : null
  const timeoutMs =
    opts.timeoutMs != null ? opts.timeoutMs : probeCfg.lan_timeout_ms
  let lanSkipReason = ''

  for (let i = 0; i < sorted.length; i++) {
    const c = sorted[i]
    const remaining = sorted.slice(i + 1)

    if (isLanDicomweb(c)) {
      if (probeCfg.skip_mixed_content && isLanHttpOnHttpsPage(c, pageProtocol)) {
        lanSkipReason = 'skip_mixed_content'
        continue
      }

      if (edgeLanBase && doProbe && !isInsecureHttpOnHttpsPage(edgeLanBase, pageProtocol)) {
        const lanOk = await probeEdgeLanHealth(edgeLanBase, {
          timeoutMs,
          fetchImpl: opts.fetchImpl,
        })
        if (lanOk) {
          return {
            candidate: c,
            reason: 'lan_base_health_ok',
            remaining,
            lanHost: originHostFromUrl(candidateDeliveryUrl(c)) || lanHint.host,
            lanSkipReason: '',
          }
        }
      }

      const wado = c.wado_url || c.wadoUrl || ''
      if (probeCfg.skip_mixed_content && isInsecureHttpOnHttpsPage(wado, pageProtocol)) {
        lanSkipReason = 'skip_mixed_content'
        continue
      }

      if (doProbe) {
        const ok = await probeLanReachable(wado, {
          timeoutMs,
          fetchImpl: opts.fetchImpl,
        })
        if (ok) {
          return {
            candidate: c,
            reason: 'lan_probe_ok',
            remaining,
            lanHost: originHostFromUrl(wado) || lanHint.host,
            lanSkipReason: '',
          }
        }
        lanSkipReason = lanSkipReason || 'probe_fail'
        continue
      }
      lanSkipReason = lanSkipReason || 'probe_disabled'
      continue
    }

    if (isR2(c) || String(c.source || '').toLowerCase() === 'dicomweb') {
      if (
        probeCfg.skip_mixed_content &&
        isInsecureHttpOnHttpsPage(candidateDeliveryUrl(c), pageProtocol)
      ) {
        continue
      }
      return {
        candidate: c,
        reason: isR2(c) ? 'r2' : 'dicomweb',
        remaining,
        lanHost: lanHint.host,
        lanSkipReason,
      }
    }
  }

  return {
    candidate: null,
    reason: 'no_usable_candidates',
    remaining: [],
    lanHost: lanHint.host,
    lanSkipReason,
  }
}

/**
 * Normaliza candidate a forma usable por App.
 * @param {object|null} candidate
 * @returns {{
 *   kind: 'r2'|'dicomweb'|null,
 *   manifestUrl?: string,
 *   pacs?: string|null,
 *   wadoUrl?: string,
 *   studyUid?: string,
 * }|null}
 */
/**
 * Meta de origen para chip developer (host real + LAN conocida).
 * @param {{ candidate?: object|null, lanHost?: string, lanSkipReason?: string }|null} picked
 * @param {{ edgeLanBase?: string|null, edgeWadoUrl?: string|null }} [pickOpts]
 * @param {object[]|null|undefined} candidates
 */
export function ticketOriginMeta(picked, pickOpts = {}, candidates) {
  const chosenUrl = candidateDeliveryUrl(picked && picked.candidate)
  const hint = collectLanHint(candidates, pickOpts)
  const group = picked && picked.candidate ? candidateGroup(picked.candidate) : ''
  return {
    originHost: originHostFromUrl(chosenUrl),
    originScope: group,
    lanHost: (picked && picked.lanHost) || hint.host || '',
    lanSkipReason: (picked && picked.lanSkipReason) || '',
  }
}

export function candidateToLoadTarget(candidate) {
  if (!candidate) return null
  if (isR2(candidate)) {
    return {
      kind: 'r2',
      manifestUrl: candidate.manifest_url || candidate.manifestUrl,
      downloadUrl: candidate.download_url || candidate.downloadUrl || null,
      pacs: candidate.pacs || candidate.pacs_id || null,
      studyUid:
        candidate.study_uid ||
        candidate.studyUid ||
        candidate.study_instance_uid ||
        candidate.studyInstanceUID ||
        null,
    }
  }
  const source = String(candidate.source || '').toLowerCase()
  if (source === 'dicomweb') {
    return {
      kind: 'dicomweb',
      wadoUrl: candidate.wado_url || candidate.wadoUrl,
      studyUid: candidate.study_uid || candidate.studyUid || candidate.study_instance_uid,
    }
  }
  return null
}
